import React, { useState, useRef } from 'react';
import { PageHero, SectionLabel } from '../components/Shared';
import { ArrowLeft, Check, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { submitLead } from '../lib/api';

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const errorSummaryRef = useRef(null);
  const [form, setForm] = useState({
    fullName: '',
    company: '',
    phone: '',
    email: '',
    challenge: '',
    selling: '',
    salesTeam: '',
    activeCampaigns: ''
  });

  const handle = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'יש להזין שם מלא';
    if (!form.email.trim()) errs.email = 'יש להזין כתובת אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'כתובת אימייל לא תקינה';
    if (!form.phone.trim()) errs.phone = 'יש להזין מספר טלפון';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast({ title: 'שדות חובה חסרים', description: 'אנא בדקו את השדות המסומנים' });
      setTimeout(() => {
        errorSummaryRef.current?.focus();
      }, 50);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, source: 'contact-form' };
      ['salesTeam', 'activeCampaigns', 'company', 'selling', 'challenge'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await submitLead(payload);
      setSubmitted(true);
      toast({ title: 'תודה רבה', description: 'נחזור אליכם תוך זמן קצר לתיאום שיחת האבחון.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'אירעה שגיאה. נסו שוב או צרו קשר במייל.';
      toast({ title: 'שגיאה בשליחה', description: typeof detail === 'string' ? detail : 'אנא בדקו את הפרטים ונסו שוב.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        className="min-h-[80vh] flex items-center justify-center pt-32 pb-20 hero-backdrop"
        aria-labelledby="thanks-heading"
      >
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center reveal" role="status" aria-live="polite">
          <div className="w-16 h-16 mx-auto bg-bronze/20 flex items-center justify-center mb-8" aria-hidden="true">
            <Check className="w-8 h-8 text-bronze" />
          </div>
          <h1 id="thanks-heading" className="display-1 text-navy mb-6">תודה רבה.</h1>
          <p className="lede text-navy/70 text-lg">
            קיבלנו את הפנייה שלכם. נציג מטעם החברה יצור אתכם קשר תוך ימים בודדים לתיאום
            שיחת אבחון אסטרטגית למערך הצמיחה של העסק.
          </p>
        </div>
      </section>
    );
  }

  const renderField = ({ id, label, value, onChange, type = 'text', required = false, fullWidth = false, autoComplete, hint }) => {
    const hasError = !!errors[id];
    return (
      <div className={`flex flex-col gap-2 ${fullWidth ? 'sm:col-span-2' : ''}`}>
        <Label htmlFor={id} className="eyebrow text-navy/60">
          {label}
          {required && <span className="text-destructive mr-1" aria-hidden="true"> *</span>}
        </Label>
        {hint && <span className="text-navy/50 text-xs -mt-1">{hint}</span>}
        <Input
          id={id}
          name={id}
          type={type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          className={`input-clean ${hasError ? 'border-b-destructive' : ''}`}
        />
        {hasError && (
          <p id={`${id}-error`} className="text-destructive text-sm flex items-center gap-1.5 mt-1" role="alert">
            <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
            {errors[id]}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHero
        eyebrow="ההצעה — שיחת אבחון אסטרטגית"
        title="מוכנים להפסיק להתפזר"
        accent="ולהתחיל לצמוח באמת?"
        subtitle="אם העסק שלכם עובד, שולי הרווח בריאים ואתם מבינים ששיווק רציני דורש ניהול מקצועי מא׳ ועד ת׳ — אנחנו צריכים לדבר."
      />

      <section className="py-20 lg:py-28 bg-cream" aria-labelledby="form-heading">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5 reveal">
              <SectionLabel>לפני הטופס</SectionLabel>
              <h2 id="form-heading" className="display-2 text-navy mb-8">
                לא שיחת מכירה. שיחת אבחון אמיתית.
              </h2>
              <div className="space-y-6 lede text-navy/75">
                <p>
                  שיחת האבחון שלנו אינה "שיחת מכירה" לסגירת קמפיין נקודתי. היא נועדה
                  להבין את מנועי הצמיחה של העסק שלכם, לאתר את צווארי הבקבוק שגורמים לכם
                  לשרוף זמן ותקציב על ספקים מפוזרים, ולבחון יחד האם המערכת של
                  Gal&Blizovsky היא הפתרון שייקח אתכם לשלב הבא.
                </p>
                <p>
                  ההכנה שלנו לשיחה מתחילה ברגע שאתם שולחים את הטופס: אנחנו מציצים בעסק,
                  לומדים את התחום, ומגיעים לשיחה עם הבנה ראשונית — כדי שהזמן שלכם ינוצל
                  לטובת ההחלטות שבאמת חשובות.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 reveal">
              <form onSubmit={submit} className="space-y-8 bg-cream-200 p-8 lg:p-12" noValidate aria-label="טופס תכלול שיווקי">
                {Object.keys(errors).length > 0 && (
                  <div
                    ref={errorSummaryRef}
                    tabIndex={-1}
                    role="alert"
                    aria-live="assertive"
                    className="bg-destructive/10 border border-destructive/40 p-4 outline-none"
                  >
                    <p className="text-destructive font-medium mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" aria-hidden="true" />
                      יש לתקן {Object.keys(errors).length} שדות
                    </p>
                    <ul className="text-destructive/90 text-sm space-y-1 mr-6 list-disc">
                      {Object.entries(errors).map(([k, v]) => (
                        <li key={k}>
                          <a href={`#${k}`} className="underline hover:text-destructive">{v}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-6">
                  {renderField({
                    id: 'fullName',
                    label: 'שם מלא',
                    value: form.fullName,
                    onChange: handle('fullName'),
                    required: true,
                    autoComplete: 'name'
                  })}
                  {renderField({
                    id: 'company',
                    label: 'שם העסק',
                    hint: 'כדי שנציץ וננתח לפני השיחה',
                    value: form.company,
                    onChange: handle('company'),
                    autoComplete: 'organization'
                  })}
                  {renderField({
                    id: 'phone',
                    label: 'טלפון',
                    value: form.phone,
                    onChange: handle('phone'),
                    type: 'tel',
                    required: true,
                    autoComplete: 'tel'
                  })}
                  {renderField({
                    id: 'email',
                    label: 'דוא״ל',
                    value: form.email,
                    onChange: handle('email'),
                    type: 'email',
                    required: true,
                    autoComplete: 'email'
                  })}
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="challenge" className="eyebrow text-navy/60">
                    מה האתגר הכי גדול שלכם היום בדיגיטל?
                  </Label>
                  <span className="text-navy/50 text-xs -mt-1">
                    למשל: ריבוי ספקים, לידים חלשים, קושי במדידה
                  </span>
                  <Textarea
                    id="challenge"
                    name="challenge"
                    value={form.challenge}
                    onChange={handle('challenge')}
                    rows={3}
                    className="bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-navy resize-none"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="selling" className="eyebrow text-navy/60">
                    מה השאיפה העסקית לשנה הקרובה?
                  </Label>
                  <Textarea
                    id="selling"
                    name="selling"
                    value={form.selling}
                    onChange={handle('selling')}
                    rows={2}
                    className="bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-navy resize-none"
                  />
                </div>

                <fieldset className="grid sm:grid-cols-2 gap-6 border-0 p-0 m-0">
                  <legend className="sr-only">מצב נוכחי בעסק</legend>
                  {[
                    { key: 'activeCampaigns', label: 'יש קמפיינים פעילים?' },
                    { key: 'salesTeam', label: 'יש צוות מכירות?' }
                  ].map((q) => (
                    <div key={q.key} className="flex flex-col gap-3" role="group" aria-labelledby={`${q.key}-label`}>
                      <span id={`${q.key}-label`} className="eyebrow text-navy/60">{q.label}</span>
                      <RadioGroup
                        value={form[q.key]}
                        onValueChange={(v) => setForm({ ...form, [q.key]: v })}
                        className="flex gap-6"
                        aria-labelledby={`${q.key}-label`}
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="yes" id={`${q.key}-y`} />
                          <Label htmlFor={`${q.key}-y`} className="text-navy text-sm cursor-pointer">כן</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="no" id={`${q.key}-n`} />
                          <Label htmlFor={`${q.key}-n`} className="text-navy text-sm cursor-pointer">לא</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  ))}
                </fieldset>

                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                  className="btn-cta inline-flex items-center gap-3 bg-navy text-cream px-8 py-5 text-sm tracking-wide hover:bg-navy-700 transition-colors duration-300 w-full sm:w-auto justify-center disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      שולח...
                    </>
                  ) : (
                    <>
                      קבעו שיחת אבחון למערך הצמיחה שלכם
                      <ArrowLeft className="arrow w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </button>

                <p className="text-navy/60 text-xs leading-relaxed">
                  בשליחת הטופס אני מאשר/ת שקיבלתי את מדיניות הפרטיות ותנאי השימוש, ומאשר/ת לשמור את הנתונים לצורך יצירת קשר.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
